import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../app/AuthContext";
import { errorMessage } from "../api/client";
import { AuthFrame } from "../components/AuthFrame";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [recovering, setRecovering] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [newRecoveryCode, setNewRecoveryCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (recovering) {
        const nextCode = await auth.recoverAccount({
          email,
          recoveryCode,
          newPassword: password,
        });
        setNewRecoveryCode(nextCode);
      } else {
        await auth.signIn({ email, password });
        navigate("/game", { replace: true });
      }
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  if (newRecoveryCode) {
    return (
      <AuthFrame
        title="访问已恢复"
        eyebrow="RECOVERY COMPLETE"
        alternate={{ to: "/register", prompt: "需要新账号？", label: "注册" }}
      >
        <RecoveryCode code={newRecoveryCode} />
        <button className="button primary full" onClick={() => navigate("/game", { replace: true })}>
          进入存档舱
        </button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame
      title={recovering ? "恢复访问权限" : "返回指挥席"}
      eyebrow={recovering ? "RECOVERY" : "AUTHENTICATION"}
      alternate={{ to: "/register", prompt: "尚未建立文明？", label: "注册新账号" }}
    >
      <form className="form-stack" onSubmit={submit}>
        <div>
          <h2>{recovering ? "恢复账号" : "登录"}</h2>
          <p className="muted">{recovering ? "输入注册时保存的一次性恢复码。" : "继续管理你的持久化星际文明。"}</p>
        </div>
        {error && <div className="alert danger" role="alert">{error}</div>}
        <label>
          <span className="field-label">邮箱</span>
          <input name="email" type="email" autoComplete="email" required value={email} onChange={(event) => setEmail(event.target.value)} />
        </label>
        {recovering && (
          <label>
            <span className="field-label">恢复码</span>
            <input name="recoveryCode" autoComplete="off" required value={recoveryCode} onChange={(event) => setRecoveryCode(event.target.value)} />
          </label>
        )}
        <label>
          <span className="field-label">{recovering ? "新密码" : "密码"}</span>
          <input
            name="password"
            type="password"
            autoComplete={recovering ? "new-password" : "current-password"}
            minLength={10}
            maxLength={128}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {recovering && <span className="field-hint">10–128 个字符</span>}
        </label>
        <button className="button primary full" type="submit" disabled={busy}>
          {busy ? "登录中…" : recovering ? "设置新密码并登录" : "登录"}
        </button>
        <button
          className="button ghost full"
          type="button"
          onClick={() => {
            setRecovering((value) => !value);
            setError(null);
            setPassword("");
          }}
        >
          {recovering ? "返回密码登录" : "使用恢复码"}
        </button>
      </form>
    </AuthFrame>
  );
}

export function RecoveryCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="form-stack">
      <div className="alert warning" role="status">
        <strong>恢复码只显示这一次</strong>
        <span>将它保存在密码管理器中。服务器不会保存可读取的原文。</span>
      </div>
      <div className="recovery-code" data-testid="recovery-code">
        <code>{code}</code>
        <button
          className="button compact"
          type="button"
          onClick={() => void navigator.clipboard.writeText(code).then(() => setCopied(true))}
        >
          {copied ? "已复制" : "复制"}
        </button>
      </div>
    </div>
  );
}
